import { z } from 'zod';
import { BlockSchema } from './blocks';
import { DesignTokensSchema } from './posts';
import { ComponentUsageSchema } from './components';
import { EmailCalendarSchema } from './email-calender';
import { EmailFooterOverrideSchema } from './email-footer';
import { VariableDefinitionsSchema } from './variable_definitions';

// ─────────────────────────────────────────────────────────────────
// Email create / update payloads (API request bodies)
// ─────────────────────────────────────────────────────────────────
export const EmailAttachmentSchema = z.object({
  filename: z.string(),
  contentType: z.string(),
  href: z.string().url(),
});

/**
 * Padding for the email body cell, in CSS shorthand (one to four lengths, e.g.
 * `24px 16px`). Restricted to plain lengths because the theme writes it straight
 * into an inline `style` attribute.
 */
const CSS_LENGTH = String.raw`(?:0|\d+(?:\.\d+)?(?:px|em|rem|%))`;
export const EmailContentPaddingSchema = z
  .string()
  .trim()
  .regex(new RegExp(`^${CSS_LENGTH}(?:\\s+${CSS_LENGTH}){0,3}$`), {
    message: 'Use 1–4 CSS lengths, e.g. "24px 16px"',
  });

export const UpdateEmailSchema = z.object({
  subject: z.string().min(1).max(500).optional(),
  previewText: z.string().optional(),
  blocks: z.array(BlockSchema).optional(),
  /**
   * Per-email footer override (see email-footer.ts). Omitted ⇒ leave the
   * stored value alone; `null` ⇒ clear it and inherit the workspace footer.
   */
  footerOverride: EmailFooterOverrideSchema.nullable().optional(),
  /** Reusable-component usage metadata (see components.ts). */
  componentUsages: z.array(ComponentUsageSchema).optional(),
  designOverrides: DesignTokensSchema.optional().nullable(),
  /**
   * Files sent with the email. Optional like every other field: this route is a
   * partial update, so an omitted list leaves the stored one untouched. The
   * panel's attachment editor always sends the complete list.
   */
  attachments: z.array(EmailAttachmentSchema).optional(),
  /** Body-cell padding. `null` ⇒ clear it and use the theme's default. */
  contentPadding: EmailContentPaddingSchema.nullable().optional(),
});

export type EmailAttachment = z.infer<typeof EmailAttachmentSchema>;

// ─────────────────────────────────────────────────────────────────
// The email document — one definition of what an email *is* to author
//
// Three surfaces move an email in and out of the product: the JSON export in
// the editor, the import dialog, and `POST /api/emails`. Each used to keep its
// own list of fields, and the lists disagreed — the export wrote four fields
// while the import read five, so exporting an email and importing it back
// silently dropped `variableDefinition`; and create's insert wrote fewer columns
// than its schema accepted, which lost the preview text the same way.
//
// A field is now added here (or nowhere), and every surface follows: what an
// export writes is what an import restores is what a create stores.
//
// Server-managed columns stay out on purpose: an import creates a *new* email,
// so `id`, `workspaceId`, `authorId`, `status` and the timestamps are the
// server's to set — an imported copy of a sent campaign is a fresh draft.
// ─────────────────────────────────────────────────────────────────
export const EmailDocumentSchema = z.object({
  subject: z.string().min(1).max(500),
  previewText: z.string().nullable().optional(),
  blocks: z.array(BlockSchema).default([]),
  /** Theme-token overrides for this email alone (see DesignTokensSchema). */
  designOverrides: DesignTokensSchema.nullable().optional(),
  /** The merge fields this email's blocks may use. */
  variableDefinition: VariableDefinitionsSchema.optional(),
  /** Body footer override; omitted inherits the workspace footer. */
  footerOverride: EmailFooterOverrideSchema.nullable().optional(),
  /** A calendar invite recipients can add from the email. */
  calendarInvite: EmailCalendarSchema.nullable().optional(),
  /** Files delivered with the email. */
  attachments: z.array(EmailAttachmentSchema).default([]),
  /** Reusable-component instances the body was built from. */
  componentUsages: z.array(ComponentUsageSchema).default([]),
  /** Id of the font row the email falls back to (see the theme's fonts). */
  defaultFont: z.string().nullable().optional(),
  /** Body-cell padding (CSS shorthand); omitted uses the theme's default. */
  contentPadding: EmailContentPaddingSchema.nullable().optional(),
});

export type EmailDocument = z.infer<typeof EmailDocumentSchema>;

/**
 * What `POST /api/emails` accepts — the same document, so an exported email can
 * be imported back without a field falling through the gap between them.
 */
export const CreateEmailSchema = EmailDocumentSchema;

/** Input side of the payload: a caller may omit anything the schema defaults. */
export type CreateEmailInput = z.input<typeof CreateEmailSchema>;
export type UpdateEmailInput = z.infer<typeof UpdateEmailSchema>;

export const SmtpConfigSchema = z.object({
  host: z.string().min(1, 'SMTP host is required'),
  port: z.coerce.number().int().positive().default(587),
  /** true = implicit TLS (usually port 465); false = STARTTLS (usually 587/25) */
  secure: z.boolean().default(false),
  user: z.string().optional(),
  pass: z.string().optional(),
  /** Sender address shown in the "From" header. Falls back to `user` server-side. */
  from: z.string().email('Invalid From address').optional(),
  /** Optional display name, e.g. "Eventor" → "Eventor <hello@eventor.com>". */
  fromName: z.string().max(100).optional(),
});

export const EmailSenderTypeSchema = z.enum(['smtp', 'brevo_api']);
export type EmailSenderType = z.infer<typeof EmailSenderTypeSchema>;

export const BrevoSenderDataSchema = z.object({
  apiKey: z.string(),
  fromEmail: z.string().email(),
  fromName: z.string().optional(),
});

export const CreateEmailSenderSchema = z.discriminatedUnion('senderType', [
  z.object({
    name: z.string().min(1, 'Sender name is required').max(255),
    description: z.string().optional(),
    senderType: z.literal('smtp'),
    data: SmtpConfigSchema,
  }),
  z.object({
    name: z.string().min(1, 'Sender name is required').max(255),
    description: z.string().optional(),
    senderType: z.literal('brevo_api'),
    data: BrevoSenderDataSchema,
  }),
]);

// ─────────────────────────────────────────────────────────────────
// Sender update — everything is optional and per-type. Secrets (SMTP
// password / Brevo API key) are never returned by the API; on update a
// blank or omitted secret keeps the stored value, a non-empty one
// replaces it. `senderType` must match the stored sender.
// ─────────────────────────────────────────────────────────────────

const SmtpSenderUpdateDataSchema = z.object({
  host: z.string().min(1).optional(),
  port: z.coerce.number().int().positive().optional(),
  secure: z.boolean().optional(),
  user: z.string().optional(),
  pass: z.string().optional(),
  from: z.string().email().optional(),
  fromName: z.string().max(100).optional(),
});

const BrevoSenderUpdateDataSchema = z.object({
  apiKey: z.string().optional(),
  fromEmail: z.string().email().optional(),
  fromName: z.string().optional(),
});

export const UpdateEmailSenderSchema = z.discriminatedUnion('senderType', [
  z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().optional().nullable(),
    senderType: z.literal('smtp'),
    isActive: z.boolean().optional(),
    data: SmtpSenderUpdateDataSchema.optional(),
  }),
  z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().optional().nullable(),
    senderType: z.literal('brevo_api'),
    isActive: z.boolean().optional(),
    data: BrevoSenderUpdateDataSchema.optional(),
  }),
]);

export type CreateEmailSenderInput = z.infer<typeof CreateEmailSenderSchema>;
export type UpdateEmailSenderInput = z.infer<typeof UpdateEmailSenderSchema>;

/** Sender as returned by the API — secret fields are stripped. */
export const EmailSenderSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  senderType: EmailSenderTypeSchema,
  isActive: z.boolean(),
  /** Non-secret config: SMTP {host,port,secure,user,from,fromName} or Brevo {fromEmail,fromName}. */
  data: z.record(z.string(), z.unknown()),
  /** True when a password (SMTP) / API key (Brevo) is stored. */
  hasSecret: z.boolean(),
  lastUsedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EmailSender = z.infer<typeof EmailSenderSchema>;

export const EmailSenderListSchema = z.object({
  data: z.array(EmailSenderSchema),
});
export type EmailSenderList = z.infer<typeof EmailSenderListSchema>;

export const TestSendEmailSchema = z
  .object({
    /** Raw SMTP details (legacy per-send path) — mutually exclusive with senderId. */
    smtp: SmtpConfigSchema.optional(),
    /** A stored sender from GET /api/emails/senders. Only SMTP senders can be used to send today. */
    senderId: z.string().uuid('Invalid sender id').optional(),
    to: z.string().email('Invalid destination email'),
    /**
     * Values for the email's `variableDefinition`, keyed by variable name.
     * Validated against those definitions with `validateVariableValues`.
     */
    variables: z.record(z.string(), z.unknown()).optional(),
  })
  .refine(({ smtp, senderId }) => Boolean(smtp) !== Boolean(senderId), {
    message: 'Provide either smtp details or a stored senderId, not both',
    path: ['senderId'],
  });

export type SmtpConfig = z.infer<typeof SmtpConfigSchema>;
export type TestSendEmailInput = z.infer<typeof TestSendEmailSchema>;

// ─────────────────────────────────────────────────────────────────
// Email status
// ─────────────────────────────────────────────────────────────────
export const EMAIL_STATUS_FILTERS = [
  'draft',
  'review',
  'scheduled',
  'sending',
  'sent',
  'archived',
  'failed',
] as const;
export const EmailStatusSchema = z.enum(EMAIL_STATUS_FILTERS);
export type EmailStatus = z.infer<typeof EmailStatusSchema>;

// ─────────────────────────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────────────────────────
export const EmailPaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: EmailStatusSchema.optional(),
  tag: z.string().optional(),
  search: z.string().optional(),
  authorId: z.string().optional(),
});

export type EmailPaginationQuery = z.infer<typeof EmailPaginationQuerySchema>;
